import React, { useState, useEffect } from "react";
import { X, User, Mail, Phone, Calendar, Clock, MapPin, AlertCircle, DollarSign, IndianRupee } from "lucide-react";
import { mainAdminApi } from "../../../config";
import toast from "react-hot-toast";

declare global {
    interface Window {
        Razorpay: any;
    }
}

// Load Razorpay script
const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

interface BookingFormData {
    name: string;
    email: string;
    phone: string;
    age: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
    age?: string;
}

interface LabTestBookingProps {
    test: any;
    userEmail?: string;
    onClose: () => void;
    onBookingSuccess: (bookingId: string) => void;
}

const LabTestBooking: React.FC<LabTestBookingProps> = ({ test, userEmail = "", onClose, onBookingSuccess }) => {
    const [formData, setFormData] = useState<BookingFormData>({
        name: "",
        email: userEmail || "",
        phone: "",
        age: "",
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState(false);



    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for this field
        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }
        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = "Phone must be 10 digits";
        if (!formData.age.trim()) newErrors.age = "Age is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBooking = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fill all required fields");
            return;
        }

        setLoading(true);
        try {
            // Load Razorpay script first
            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                toast.error("Failed to load payment gateway. Please try again.");
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams(window.location.search);
            const urlVendorId = queryParams.get("vendorId") || queryParams.get("mentorId");
            if (urlVendorId) {
                sessionStorage.setItem("referralVendorId", urlVendorId);
            }
            const referralVendorId = urlVendorId || sessionStorage.getItem("referralVendorId") || undefined;

            // Create Razorpay Order
            const orderResponse = await mainAdminApi.post<any>("/lab-test/booking/create-order", {
                vendorId: test.vendorId,
                referralVendorId: referralVendorId,
                testId: test._id,
                amount: test.price,
                email: formData.email,
                phone: formData.phone,
                name: formData.name,
            });
            const { orderId, bookingId, amount, currency } = (orderResponse.data as any);

            // Open Razorpay Checkout
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
                order_id: orderId,
                amount: amount * 100,
                currency: currency,
                name: "Metafit Wellness",
                description: `Lab Test - ${test.name}`,
                prefill: {
                    name: formData.name,
                    email: formData.email,
                    contact: formData.phone,
                },
                handler: async (response: any) => {
                    try {
                        // Verify payment on backend
                        const verifyResponse = await mainAdminApi.post<any>(
                            "/lab-test/booking/verify-payment",
                            {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                                bookingId: bookingId,
                                bookingDetails: formData,
                            }
                        );

                        const verifyData = (verifyResponse.data as any);
                        if (verifyData.success) {
                            toast.success("Lab test booked successfully! 🎉\nYou'll receive a confirmation on WhatsApp");
                            onBookingSuccess(verifyData.bookingId);
                            onClose();
                            // Navigate to success page
                            setTimeout(() => {
                                window.location.href = `/booking-success/${verifyData.bookingId}`;
                            }, 1500);
                        } else {
                            toast.error("Payment verification failed");
                        }
                    } catch (error: any) {
                        console.error("Verification error:", error);
                        toast.error("Payment verification failed");
                    }
                },
                modal: {
                    ondismiss: () => {
                        setLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
            setLoading(false);
        } catch (error: any) {
            console.error("Booking error:", error);
            toast.error(error.response?.data?.message || "Failed to create booking");
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-blue-600 px-6 py-6 flex justify-between items-center text-white">
                    <div>
                        <h2 className="text-2xl font-bold">Book {test.name}</h2>
                        <p className="text-teal-100 text-sm">₹{test.price}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleBooking} className="p-6 space-y-6">
                    {/* Test Summary */}
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">Test Details</h3>
                        <div className="space-y-1 text-sm">
                            <p>
                                <span className="text-gray-600">Category:</span>{" "}
                                <span className="font-medium">{test.category}</span>
                            </p>
                            <p>
                                <span className="text-gray-600">Sample Type:</span>{" "}
                                <span className="font-medium">{test.sampleType}</span>
                            </p>
                            <p>
                                <span className="text-gray-600">Report Time:</span>{" "}
                                <span className="font-medium">{test.reportTime}</span>
                            </p>
                        </div>
                    </div>

                    {/* Personal Information */}
                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900">Personal Information</h3>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <User className="inline w-4 h-4 mr-1" />
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter your full name"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
                                    errors.name ? "border-red-500" : "border-gray-300"
                                }`}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Mail className="inline w-4 h-4 mr-1" />
                                    Email (Optional)
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="your@email.com"
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
                                        errors.email ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Phone className="inline w-4 h-4 mr-1" />
                                    Phone *
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="10-digit number"
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
                                        errors.phone ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Age *
                            </label>
                            <input
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={handleInputChange}
                                placeholder="Enter your age"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
                                    errors.age ? "border-red-500" : "border-gray-300"
                                }`}
                            />
                            {errors.age && (
                                <p className="text-red-500 text-sm mt-1">{errors.age}</p>
                            )}
                        </div>
                    </div>



                    {/* Price Summary */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Test Price:</span>
                            <span className="font-semibold text-gray-900">₹{test.price}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Original Price:</span>
                            <span className="text-sm text-gray-500 line-through">₹{test.originalPrice}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between items-center">
                            <span className="font-semibold text-gray-900">Total Amount:</span>
                            <span className="text-2xl font-bold text-teal-600">₹{test.price}</span>
                        </div>
                    </div>

                    {/* Booking Terms */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                        <p className="flex gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                Sample will be collected from your preferred location on the selected date
                                and time. Report will be available within {test.reportTime}.
                            </span>
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-6 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <IndianRupee className="w-4 h-4" />
                            {loading ? "Processing..." : "Proceed to Payment"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LabTestBooking;
